import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Mail,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  Trash2,
  ChevronDown,
  Plus,
  RefreshCw,
  Zap,
} from "lucide-react";
import gmailService from "../../services/gmail.service";
import NodeDocsButton from "../../docs/NodeDocsButton";
import syntaxHighlight from "../../utils/jsonViewer";

type CredentialItem = {
  _id: string;
  user_info: {
    user_email: string;
    user_name: string;
  };
};

export const GmailTriggerNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes, deleteElements } = useReactFlow();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const authPopupRef = useRef<Window | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(
    data?.gmail_credential_id || "",
  );

  const [credentials, setCredentials] = useState<CredentialItem[]>([]);

  const stopPropagation = useCallback((e: any) => e.stopPropagation(), []);

  // Listen for auth success message from popup
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const allowed = [
        "http://localhost:3000",
        "http://localhost:5173",
        "https://nightly-dms.skill-mine.com",
        "https://demo-dms.skill-mine.com",
      ];

      if (!allowed.includes(event.origin)) return;

      if (
        event.data?.type === "GOOGLE_AUTH_SUCCESS" ||
        event.data?.status === "success"
      ) {
        setConnecting(false);
        // Refresh list
        loadCredentials();

        const popup = authPopupRef.current;
        if (popup && !popup.closed) popup.close();
      }
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  // Poll for popup closure to refresh list
  useEffect(() => {
    if (!connecting) return;

    const pollInterval = setInterval(() => {
      const popup = authPopupRef.current;

      if (popup && popup.closed) {
        authPopupRef.current = null;
        setConnecting(false);
        // Refresh credentials when popup closes, assuming user might have added one
        loadCredentials();
        clearInterval(pollInterval);
      }
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [connecting]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as any)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openAuthPopup = async () => {
    try {
      setConnecting(true);
      setErrorMsg(null);

      const templateId =
        data?.template_id ||
        data?.request_id ||
        localStorage.getItem("template_id");

      console.log("🔑 Opening Gmail auth with template_id:", templateId);

      const authUrl = gmailService.getGmailLoginUrl(templateId);
      console.log("🌐 Auth URL:", authUrl);

      const width = 500;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        "GmailAuthPopup",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
      );

      if (!popup) {
        throw new Error("Popup blocked! Please allow popups for this site.");
      }

      authPopupRef.current = popup;
      popup.focus();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to open login popup");
      setConnecting(false);
    }
  };

  const loadCredentials = async () => {
    if (loadingCredentials) return;
    const templateId = data?.template_id || localStorage.getItem("template_id");

    try {
      setLoadingCredentials(true);
      setErrorMsg(null);

      const response = await gmailService.listCredentials(templateId);
      console.log("Attributes list response:", response);

      if (response && response.success) {
        setCredentials(response.data || []);
      } else {
        setErrorMsg("Failed to load credentials");
      }
      setHasLoadedCredentials(true);
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to load credentials";
      setErrorMsg(errorMsg);
    } finally {
      setLoadingCredentials(false);
    }
  };

  // Load credentials when modal opens
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    if (!hasLoadedCredentials && !loadingCredentials) {
      loadCredentials();
    }
  }, [isModalOpen]);

  // Also load credentials on mount if there's a credential ID but no stored info
  useEffect(() => {
    if (
      selectedCredentialId &&
      !data?.selected_credential_info &&
      !hasLoadedCredentials &&
      !loadingCredentials
    ) {
      loadCredentials();
    }
  }, []);

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  // Store credential info when credential is selected
  const selectedCredentialInfo = useMemo(() => {
    const cred = credentials.find((c) => c._id === selectedCredentialId);
    return cred?.user_info || null;
  }, [credentials, selectedCredentialId]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;

        const updatedData: any = {
          ...(n.data as object),
          inputs: {
            ...((n.data as any).inputs || {}),
            gmail_credential_id: selectedCredentialId,
          },
          gmail_credential_id: selectedCredentialId,
        };

        // Store credential info for display when credentials aren't loaded
        if (selectedCredentialInfo) {
          updatedData.selected_credential_info = selectedCredentialInfo;
        }

        return { ...n, data: updatedData };
      }),
    );
  }, [selectedCredentialId, selectedCredentialInfo, id, setNodes]);

  const validationStatus = useMemo(() => {
    const errors: string[] = [];
    if (!selectedCredentialId) errors.push("Gmail credential required");

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [selectedCredentialId]);

  const hasConfig = validationStatus.isValid;

  const statusConfig = useMemo(() => {
    return hasConfig
      ? { bg: "bg-emerald-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [hasConfig]);

  const selectedCredential = useMemo(() => {
    // First try to find from loaded credentials
    const fromList = credentials.find((c) => c._id === selectedCredentialId);
    if (fromList) return fromList;

    // Fallback to stored credential info in node data
    if (selectedCredentialId && data?.selected_credential_info) {
      return {
        _id: selectedCredentialId,
        user_info: data.selected_credential_info,
      };
    }
    return undefined;
  }, [credentials, selectedCredentialId, data?.selected_credential_info]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <>
      <div
        className={`relative group border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
          !validationStatus.isValid
            ? "bg-white border-red-500 shadow-red-200"
            : hasConfig
              ? "bg-red-50/30 border-red-400 shadow-red-100"
              : selected
                ? "bg-white border-red-500 shadow-md"
                : "bg-white border-red-200 hover:border-red-400"
        }`}
        onClick={stopPropagation}
      >
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-red-600 border-2 border-white"
          style={{
            boxShadow: "0 0 0 4px rgba(220, 38, 38, 0.25)",
          }}
        />

        {/* Trigger indicator - minimal corner notch design */}
        <div className="absolute -top-[2px] -left-[2px] z-10">
          <div className="flex items-center">
            <div className="bg-red-400 text-white text-[8px] font-semibold tracking-wider px-2 py-0.5 rounded-tl-[10px] rounded-br-lg flex items-center gap-1">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
              </span>
              TRIGGER
            </div>
          </div>
        </div>

        <div className="flex items-start justify-between mb-3 cursor-move mt-1">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-red-100">
              <Mail className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-800 text-sm">
                Gmail Trigger
              </h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                    statusConfig.pulse ? "animate-pulse" : ""
                  }`}
                />
                <p className="text-xs text-gray-600">{statusConfig.text}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NodeDocsButton nodeType="GMAIL_CONNECTOR" />
            <button
              onClick={handleDeleteClick}
              className="text-red-500 hover:text-gray-700 p-1 rounded transition"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!validationStatus.isValid && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-900 mb-1">
                  Configuration Required
                </p>
                <ul className="text-red-700 space-y-0.5">
                  {validationStatus.errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3" onClick={stopPropagation}>
          <button
            onClick={() => setIsModalOpen(true)}
            className={`w-full py-2.5 rounded-lg text-white text-xs font-medium flex justify-center items-center gap-2 shadow-sm transition-colors ${
              hasConfig
                ? "bg-red-700 hover:bg-red-800"
                : "bg-red-700 hover:bg-red-800"
            }`}
          >
            <Settings className="w-3.5 h-3.5" />
            {hasConfig ? "Edit Configuration" : "Configure Trigger"}
          </button>
        </div>

        {selectedCredential ? (
          <div className="mt-2 border-t border-gray-100">
            {/* Credential display with Gmail icon */}
            <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-lg">
              <div className="truncate flex-1 min-w-0">
                <p className="text-[11px] font-medium text-gray-800 truncate">
                  {selectedCredential.user_info.user_name}
                </p>
                <p className="text-[10px] text-gray-500 truncate">
                  {selectedCredential.user_info.user_email}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 italic text-center">
              Select a credential to enable auto-trigger
            </p>
          </div>
        )}
      </div>

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded">
                    <Mail className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Gmail Trigger
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Auto-triggers workflow when new emails are received
                    </p>
                  </div>
                </div>
                <button
                  className="text-gray-400 p-1"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* 2. Middle Panel: Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6 min-w-[250px]">
                  {!validationStatus.isValid && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Configuration Issues
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                            {validationStatus.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Gmail Trigger Configuration
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Select a Gmail credential to monitor for incoming messages
                  </p>

                  {/* Trigger behavior info box */}

                  <div className="space-y-5">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Credentials
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Select which Gmail account to use for this trigger.
                      </p>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Credential
                        </label>
                        <div className="relative" ref={dropdownRef}>
                          <div
                            onClick={() => {
                              if (!isDropdownOpen) {
                                loadCredentials();
                              }
                              setIsDropdownOpen(!isDropdownOpen);
                            }}
                            className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-red-400 focus:ring-2 focus:ring-red-500 transition-all cursor-pointer flex items-center justify-between shadow-sm"
                          >
                            <span
                              className={`truncate ${
                                selectedCredentialId
                                  ? "text-gray-900 font-medium"
                                  : "text-gray-400"
                              }`}
                            >
                              {selectedCredential
                                ? `${selectedCredential.user_info.user_name} (${selectedCredential.user_info.user_email})`
                                : "Select a credential..."}
                            </span>
                            <ChevronDown
                              className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${
                                isDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>

                          {isDropdownOpen && (
                            <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg border border-gray-100 py-1 text-sm animate-in fade-in zoom-in-95 duration-100">
                              <div className="max-h-60 overflow-y-auto">
                                {/* Simple loader */}
                                {loadingCredentials && (
                                  <div className="px-4 py-5 flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                    <span className="text-xs text-gray-500">
                                      Loading...
                                    </span>
                                  </div>
                                )}
                                {/* Empty state with illustration */}
                                {!loadingCredentials &&
                                  credentials.length === 0 && (
                                    <div className="px-4 py-2 text-center">
                                      <p className="text-sm font-medium text-gray-600 mb-1">
                                        No credentials yet
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        Add a Gmail account to get started
                                      </p>
                                    </div>
                                  )}
                                {/* Credentials list */}
                                {!loadingCredentials &&
                                  credentials.map((cred) => (
                                    <div
                                      key={cred._id}
                                      onClick={() => {
                                        setSelectedCredentialId(cred._id);
                                        setIsDropdownOpen(false);
                                      }}
                                      className={`px-3 py-2 cursor-pointer flex items-center gap-3 group transition-colors ${
                                        selectedCredentialId === cred._id
                                          ? "bg-red-50 text-red-700"
                                          : "hover:bg-gray-50 text-gray-700"
                                      }`}
                                    >
                                      {/* Gmail avatar */}
                                      <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                          selectedCredentialId === cred._id
                                            ? "bg-red-100"
                                            : "bg-gray-100 group-hover:bg-gray-200"
                                        }`}
                                      >
                                        <Mail
                                          className={`w-4 h-4 ${
                                            selectedCredentialId === cred._id
                                              ? "text-red-600"
                                              : "text-gray-500"
                                          }`}
                                        />
                                      </div>
                                      <div className="truncate flex-1">
                                        <div className="font-medium">
                                          {cred.user_info.user_name}
                                        </div>
                                        <div className="text-xs text-gray-500 group-hover:text-gray-600">
                                          {cred.user_info.user_email}
                                        </div>
                                      </div>
                                      {selectedCredentialId === cred._id && (
                                        <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center shrink-0">
                                          <svg
                                            className="w-3 h-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={3}
                                              d="M5 13l4 4L19 7"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                              </div>
                              <div className="border-t border-gray-100 mt-1 pt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAuthPopup();
                                    setIsDropdownOpen(false);
                                  }}
                                  disabled={connecting}
                                  className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 font-medium"
                                >
                                  {connecting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Plus className="w-4 h-4" />
                                  )}
                                  {connecting
                                    ? "Connecting..."
                                    : "Create new credential"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end mt-2">
                          <button
                            onClick={loadCredentials}
                            className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1 transition-colors"
                          >
                            <RefreshCw
                              className={`w-3 h-3 ${
                                loadingCredentials ? "animate-spin" : ""
                              }`}
                            />
                            {loadingCredentials
                              ? "Refreshing..."
                              : "Refresh List"}
                          </button>
                        </div>
                      </div>

                      {errorMsg && (
                        <p className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100 flex items-center gap-2">
                          <AlertTriangle className="w-3 h-3" />
                          {errorMsg}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          How this trigger works
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Once configured, this node will automatically start
                          the workflow whenever a new email arrives in the
                          selected Gmail inbox. The email data (sender, subject,
                          body, attachments) will be passed to connected nodes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Right Sidebar: Outputs */}
                <div className="w-[400px] bg-gray-50 overflow-y-auto p-4 shrink-0 border-l border-gray-200">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Node Outputs
                    </h3>
                    <p className="text-xs text-gray-500">
                      Available after execution
                    </p>
                  </div>

                  {hasOutputs ? (
                    <div className="space-y-3">
                      {Object.entries(data.outputs).map(
                        ([key, value]: [string, any]) => (
                          <div
                            key={key}
                            className="bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-all"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-sm font-semibold text-gray-900">
                                  {key}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">
                                {typeof value}
                              </span>
                            </div>
                            <pre
                              className="text-xs font-mono break-all whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{
                                __html: syntaxHighlight(value),
                              }}
                            />
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 font-medium mb-1">
                        No outputs yet
                      </p>
                      <p className="text-xs text-gray-500">
                        Run the workflow to generate outputs
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    disabled={!validationStatus.isValid}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default GmailTriggerNode;
